/**
 * initSupabaseAuth - wires Supabase session events into authEngine + permissionEngine.
 *
 * Call once at application startup (before any protected render):
 *
 *   const teardown = initSupabaseAuth();
 *   // later, on unmount or test cleanup:
 *   teardown();
 *
 * What this does:
 *   1. Registers SupabaseAuthProvider with authEngine.
 *   2. Subscribes to supabase.auth.onAuthStateChange() so externally-driven
 *      session changes (OAuth redirects, multi-tab sign-in/out, token refresh)
 *      are reflected in authEngine without a manual signIn() call.
 *   3. Subscribes to authEngine.onAuthStateChange() and propagates the live
 *      user + roles into permissionEngine so CRUD/action enforcement stays
 *      in sync with the Supabase session throughout the app lifetime.
 *   4. Returns a teardown function that cleans up all subscriptions.
 *
 * Role resolution:
 *   user_metadata.roles (string[]) → permissionEngine.setUser()
 *   Authenticated users without explicit roles receive ["user"].
 *   Unauthenticated state → ["public"].
 */

import { getSupabase }                      from "@/lib/supabase";
import { authEngine, type AuthUser }        from "./authEngine";
import { permissionEngine }                 from "@/appEngine/permissionEngine";
import { SupabaseAuthProvider, mapUser, mapSession } from "./supabaseAuthProvider";
import { sendDeviceHeartbeat }              from "@/security/deviceHeartbeat";

// ─── Role extraction ──────────────────────────────────────────────────────────

function rolesFromUser(user: AuthUser): string[] {
  const roles = user.metadata?.roles;
  if (Array.isArray(roles) && roles.length > 0 && roles.every(r => typeof r === "string")) {
    return roles as string[];
  }
  return ["user"];
}

// ─── Init ─────────────────────────────────────────────────────────────────────

export function initSupabaseAuth(): () => void {
  authEngine.setProvider(new SupabaseAuthProvider());

  // ── Bridge Supabase session events → authEngine ──
  const { data: { subscription } } = getSupabase().auth.onAuthStateChange(
    (_event, session) => {
      if (session?.user) {
        authEngine.injectSession(mapUser(session.user), mapSession(session));
        // NTISP: register this device (trusted devices, login history,
        // new-device detection). Fire-and-forget, once per page session.
        sendDeviceHeartbeat();
      } else {
        authEngine.clearSession();
      }
    },
  );

  // ── Bridge authEngine user changes → permissionEngine ──
  const unsubAuth = authEngine.onAuthStateChange((user: AuthUser | null) => {
    if (user) {
      permissionEngine.setUser({ userId: user.id, roles: rolesFromUser(user) });
    } else {
      permissionEngine.setUser({ userId: "anonymous", roles: ["public"] });
    }
  });

  return () => {
    subscription.unsubscribe();
    unsubAuth();
  };
}
